import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { IChatMessage } from '../util/types';
import { VITE_WEBSOCKET_URL } from '../config';
import { BoxKeyPair, box } from 'tweetnacl';
import { encrypt, formatChatString, generateKeyPair } from '../util/crypto';
import { fetchTherapistKey, fetchUserAccountRemote } from '../util/UserAccount';
import { IdToken, useAuth0 } from '@auth0/auth0-react';
import { useAuthFetchFromRestAPI } from '../util/api';
import { decodeBase64 } from 'tweetnacl-util';
import { DateTime } from 'luxon';
import { ChatHeadDto } from '../../../shared/dtos/chat.dto';

type SendMessageParams = {
  text: string;
  therapy: ChatHeadDto;
};

type ChatContextType = {
  messages: IChatMessage[];
  initConnection: () => Promise<void>;
  loadMessages: (therapy: ChatHeadDto) => Promise<void>;
  closeMessages: () => void;
  loadMoreMessages: () => Promise<void>;
  sendMessage: (params: SendMessageParams) => Promise<string>;
  resendMessage: (params: SendMessageParams) => Promise<string>;
  isConnected: boolean;
  hasMore: boolean;
  isLoadingMessages: boolean;
  tKeyPair: BoxKeyPair | null;
  patientPublicKey: string | null;
  websocket: WebSocket | null;
  chatHeadMap: Map<string, IChatMessage>;
  unreadPatientIds: Set<string>;
};

const PING_INTERVAL_MS: number = 25_000;
const PONG_TIMEOUT_MS: number  = 10_000;
const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = (): ChatContextType => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used within a ChatContext');
  }
  return ctx;
};

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const wsRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const pongTimeoutRef = useRef<number | null>(null);
  const openChatPatientIdRef = useRef<string | null>(null);
  const { getIdTokenClaims, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { authFetch } = useAuthFetchFromRestAPI();

  const [chatHeadMap, setChatHeadMap] = useState<Map<string, IChatMessage>>(new Map());
  const [unreadPatientIds, setUnreadPatientIds] = useState<Set<string>>(new Set());
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [patientPublicKey, setPatientPublicKey] = useState<string | null>(null);
  const [tKeyPair, setTKeyPair] = useState<BoxKeyPair | null>(null);
  const [page, setPage] = useState(1);
  const [currentTherapyId, setCurrentTherapyId] = useState<string | null>(null);
  const pageSize: number = 25;

  const updateChatHeadMap = (chatId: string, meta: IChatMessage): void => {
    setChatHeadMap(prev => {
      const newMap = new Map(prev);
      newMap.set(chatId, meta);
      return newMap;
    });
  };

  const markAsUnread = (patientId: string): void =>
    setUnreadPatientIds((prev: Set<string>) => new Set(prev).add(patientId));

  const markAsRead = (patientId: string): void =>
    setUnreadPatientIds((prev: Set<string>) => {
      const next: Set<string> = new Set(prev);
      next.delete(patientId);
      return next;
    });

  const closeSocket: () => void = (): void => {
    clearHeartbeat();
    setIsConnected(false);

    try {
      wsRef.current?.close();
    } catch { /* empty */ }

    wsRef.current = null;
  };

  const initConnection: () => Promise<void> = async (): Promise<void> => {

    if (wsRef.current) {
      return;
    }

    const token: string = await getAccessTokenSilently();
    const therapistId: string | undefined = (await fetchUserAccountRemote(authFetch))?._id;
    const idToken: IdToken | undefined = await getIdTokenClaims();

    if (idToken) {
      const keyData: string | null = await fetchTherapistKey(authFetch, idToken);
      if (keyData) {
        setTKeyPair(generateKeyPair(keyData.toString()));
      }
    }

    const socket: WebSocket = new WebSocket(VITE_WEBSOCKET_URL + '/t:' + therapistId, token);

    socket.onopen = (): void => {
      setIsConnected(true);
      wsRef.current = socket;
      
      startHeartbeat();
    };
    socket.onmessage = (messageEvent): void => {
      const message = JSON.parse(messageEvent.data);
      if (!message || !('messageType' in message)) {
        return;
      }

      if (message.messageType === 'PONG') {
        return;
      }

      if (message.messageType === 'ACK') {
        handleAck(message.ackId);
        return;
      }

      updateChatHeadMap(message.senderId, message);
      if (message.senderId === openChatPatientIdRef.current) {
        setMessages((prev: IChatMessage[]) => [message, ...prev]);
      } else {
        markAsUnread(message.senderId);
      }
    };
    socket.onclose = (): void => {
      closeSocket();
    };
    socket.onerror = (): void => {
      closeSocket();
    };
  };

  const handleAck = (ackId: string): void =>
    setMessages((prev: IChatMessage[]) => prev.map((msg: IChatMessage) => (msg.ackId === ackId ? { ...msg, ackId: undefined } : msg)));

  const loadMessages = async (therapy: ChatHeadDto): Promise<void> => {
    setIsLoadingMessages(true);
    setMessages([]);
    setPage(1);
    setHasMore(true);
    setCurrentTherapyId(therapy.id);
    openChatPatientIdRef.current = therapy.patientId;
    markAsRead(therapy.patientId);

    try {
      const chatHead = await authFetch(`/api/v1/chat/${therapy.id}/1/${pageSize}`);
      if (chatHead?.messages) {
        setPatientPublicKey(chatHead.patientKey);
        setMessages(chatHead.messages);
        setHasMore(chatHead.messages.length === pageSize);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const closeMessages = (): void => {
    openChatPatientIdRef.current = null;
  };

  const getMessagesPage = async (pageNo: number): Promise<void> => {
    if (!currentTherapyId) {
      return;
    }

    const chatHead = await authFetch(`/api/v1/chat/${currentTherapyId}/${pageNo}/${pageSize}`);
    if (chatHead?.messages) {
      setPatientPublicKey(chatHead.patientKey);
      if (chatHead.messages.length > 0) {
        setMessages((prev: IChatMessage[]) => [...prev, ...chatHead.messages]);
      } else {
        setHasMore(false);
      }
    }
  };

  const loadMoreMessages = async (): Promise<void> => {
    if (!hasMore || isLoadingMessages) {
      return;
    }

    const nextPage: number = page + 1;
    setIsLoadingMessages(true);

    try {
      await getMessagesPage(nextPage);
      setPage(nextPage);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const sendMessage = async ({ text, therapy }: SendMessageParams): Promise<string> => {
    if (!tKeyPair || !patientPublicKey) {
      return '';
    }

    const ackId: string = Date.now().toString();
    const finalKeyTherapist: Uint8Array = box.before(decodeBase64(patientPublicKey), tKeyPair.secretKey);
    const actualText: string = formatChatString(text);

    const message: IChatMessage = {
      createdAt: DateTime.now().toISO() ?? '',
      messageType: 'TEXT',
      senderId: therapy.therapistId,
      text: encrypt(finalKeyTherapist, actualText),
      ackId,
      isNew: true,
    };

    const chat = {
      therapyId: therapy.id,
      connectionId: therapy.patientId,
      message,
    };

    wsRef.current?.send(JSON.stringify(chat));
    setMessages((prev: IChatMessage[]) => [message, ...prev]);
    updateChatHeadMap(therapy.patientId, message);

    return ackId;
  };

  const resendMessage = async ({ text, therapy }: SendMessageParams): Promise<string> => {
    if (!patientPublicKey || !tKeyPair) {
      return '';
    }

    const finalKeyTherapist = box.before(
      decodeBase64(patientPublicKey),
      tKeyPair.secretKey
    );

    const actualText: string = formatChatString(text);
    const ackId: string = DateTime.now().toUnixInteger().toString();

    const message: IChatMessage = {
      messageType: 'TEXT',
      senderId: therapy.therapyId,
      text: encrypt(finalKeyTherapist, actualText),
      ackId,
      isNew: true,
      createdAt: DateTime.now().toISO() ?? '',
    };

    const chat = { therapyId: therapy.id, connectionId: therapy.patientId, message };

    wsRef.current?.send(JSON.stringify(chat));
    setMessages((prev: IChatMessage[]) => [message, ...prev.slice(1)]);
    updateChatHeadMap(therapy.patientId, message);

    return ackId;
  };

  const clearHeartbeat: () => void = (): void => {
    if (pingTimerRef.current !== null) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (pongTimeoutRef.current !== null) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  };

  const startHeartbeat: () => void = (): void => {
    clearHeartbeat();

    pingTimerRef.current = window.setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return;
      }

      try {
        wsRef.current.send(JSON.stringify({ messageType: 'PING' } ));
      } catch {
        try {
          wsRef.current.close();
        } catch { /* empty */ }
      }
    }, PING_INTERVAL_MS);
  };
  
  useEffect(() => {
    if (isAuthenticated) {
      void initConnection();
    } else {
      closeSocket();
    }

    return (): void => closeSocket();
  }, [isAuthenticated]);

  return (
    <ChatContext.Provider value={{
      messages,
      initConnection,
      loadMessages,
      closeMessages,
      loadMoreMessages,
      sendMessage,
      resendMessage,
      isConnected,
      hasMore,
      isLoadingMessages,
      tKeyPair,
      patientPublicKey,
      websocket: wsRef.current,
      chatHeadMap,
      unreadPatientIds
    }}>
      {children}
    </ChatContext.Provider>
  );
};
